import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';

const formatCurrency = (cents) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format((Number(cents || 0) || 0) / 100);

const formatDateRange = (start, end) => {
  if (!start && !end) return '-';
  const from = start ? new Date(start).toLocaleDateString('pt-BR') : '-';
  const to = end ? new Date(end).toLocaleDateString('pt-BR') : '-';
  return `${from} ate ${to}`;
};

const PlanoPagamento = () => {
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [items, setItems] = useState([]);
  const [entitlements, setEntitlements] = useState([]);
  const [payments, setPayments] = useState([]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [plansRs, subRs] = await Promise.all([axios.get('/api/billing/plans'), axios.get('/api/billing/subscription')]);
      setPlans(Array.isArray(plansRs.data) ? plansRs.data : []);
      setSubscription(subRs.data?.subscription || null);
      setItems(Array.isArray(subRs.data?.items) ? subRs.data.items : []);
      setEntitlements(Array.isArray(subRs.data?.entitlements) ? subRs.data.entitlements : []);
      setPayments(Array.isArray(subRs.data?.payments) ? subRs.data.payments : []);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar plano e pagamento');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const runReconcile = async () => {
    try {
      await axios.post('/api/billing/reconcile');
      toast.success('Reconciliacao executada');
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro na reconciliacao');
    }
  };

  return (
    <div className="animate-fade-in space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Plano e Pagamento</h1>
          <p className="text-gray-600">Status da assinatura, modulos contratados e historico financeiro da empresa</p>
        </div>
        <button className="btn-secondary" onClick={runReconcile} disabled={loading}>
          Reconciliar agora
        </button>
      </div>

      <div className="card">
        <div className="card-body">
          <p className="text-sm text-gray-500">Status atual da assinatura</p>
          <p className="mt-2 text-2xl font-bold text-gray-900">{subscription?.status || 'sem assinatura'}</p>
          <p className="mt-1 text-sm text-gray-600">
            Plano: {subscription?.plan_nome || '-'} {subscription?.plan_price_cents ? `(${formatCurrency(subscription.plan_price_cents)}/mes)` : ''}
          </p>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-semibold">Planos disponiveis</h3>
        </div>
        <div className="card-body p-0">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Plano</th>
                  <th>Preco</th>
                  <th>Modulos</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {plans.map((plan) => (
                  <tr key={plan.id}>
                    <td>{plan.nome}</td>
                    <td>{formatCurrency(plan.price_cents)}</td>
                    <td>{Array.isArray(plan.module_keys) && plan.module_keys.length > 0 ? plan.module_keys.join(', ') : '-'}</td>
                    <td>
                      <span className={plan.is_active ? 'badge-success' : 'badge-danger'}>{plan.is_active ? 'Ativo' : 'Inativo'}</span>
                    </td>
                  </tr>
                ))}
                {!plans.length && (
                  <tr>
                    <td colSpan={4} className="py-6 text-center text-gray-500">
                      Nenhum plano cadastrado
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-semibold">Itens da assinatura</h3>
        </div>
        <div className="card-body p-0">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Modulo</th>
                  <th>Status</th>
                  <th>Valor</th>
                  <th>Periodo</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td>{item.module_key}</td>
                    <td>{item.status}</td>
                    <td>{formatCurrency(item.price_cents)}</td>
                    <td>{formatDateRange(item.current_period_start, item.current_period_end)}</td>
                  </tr>
                ))}
                {!items.length && (
                  <tr>
                    <td colSpan={4} className="py-6 text-center text-gray-500">
                      Nenhum item de assinatura encontrado
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-semibold">Entitlements atuais</h3>
        </div>
        <div className="card-body">
          <div className="flex flex-wrap gap-2">
            {entitlements.map((item) => (
              <span key={item.key} className={item.enabled ? 'badge-success' : 'badge-danger'}>
                {item.nome}: {item.status}
              </span>
            ))}
            {!entitlements.length && <p className="text-sm text-gray-500">Nenhum entitlement carregado</p>}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-semibold">Ultimos pagamentos</h3>
        </div>
        <div className="card-body p-0">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Valor</th>
                  <th>Status</th>
                  <th>Provedor</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((pay) => (
                  <tr key={pay.id}>
                    <td>{pay.created_at ? new Date(pay.created_at).toLocaleString('pt-BR') : '-'}</td>
                    <td>{formatCurrency(pay.amount_cents)}</td>
                    <td>{pay.status}</td>
                    <td>{pay.provider || '-'}</td>
                  </tr>
                ))}
                {!payments.length && (
                  <tr>
                    <td colSpan={4} className="py-6 text-center text-gray-500">
                      Nenhum pagamento encontrado
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanoPagamento;
